import { FC, useCallback, useEffect, useMemo, useRef, RefObject } from 'react';
import _, { isEqual } from 'lodash';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { VerticalPageJobType } from 'modules/job-types/types';
import { logEvent } from 'utils/analytics';
import { PropsFromRedux } from './ListPros.container';
import ProCard from './ProCard';
import CardListV2 from 'components/organisms/CardList/CardListV2';
import {
  transformFilterQueryStringToFilterState,
  unrollMatchingCriteriaFiltersToString,
} from 'modules/marketing-pages/utils';
import type { LandingPagePro } from 'modules/marketing-pages/marketing-page.types';
import { getAsPathURLString } from '../LandingPage/LandingPageDefault';
import type { RootState } from 'store/types';
import type { FilterState } from 'modules/marketing-pages/duck/types';

export const NUM_PROS_PER_PAGE = 12;
export const DEFAULT_PAGE = 1;

interface Props {
  city?: { _id: string; id: string; key: string; name: string };
  cityGroupName: string;
  currentPage: number;
  isGroupCity: boolean;
  isReviewsQAEnabled: boolean;
  jobType: VerticalPageJobType;
  landingPageFocusElement: RefObject<HTMLDivElement> | null;
  numOfProsPerPage: number;
  onLoadMore: (param: {
    day?: string;
    matchingCriteria?: string;
    page: number;
    priceRange?: string;
    pricingMenuItemKey?: string;
    size: number;
    skip: number;
    time?: string;
  }) => void;
  prefecture?: { _id: string; id: string; key: string; name: string };
  translationId: string;
  updatePageNumber: (pageNumber: number) => void;
}

const ListPros: FC<Props & PropsFromRedux> = ({
  jobType,
  currentPage,
  landingPageFocusElement,
  onLoadMore,
  proCount,
  pros,
  updatePageNumber,
  numOfProsPerPage,
  isGroupCity,
  isReviewsQAEnabled,
}) => {
  const { query, asPath } = useRouter();
  const url = useMemo(() => new URL(getAsPathURLString(asPath)), [asPath]);
  const filters = useSelector((state: RootState) => state.marketingPages.filters);
  const pageQuery = useMemo(() => (isGroupCity ? 1 : Number(query.page ?? `${DEFAULT_PAGE}`)), [
    query.page,
  ]);
  const pricingMenuItemKeyQuery = useMemo(
    () => (typeof query.pricingMenuItemKey === 'string' ? query.pricingMenuItemKey : ''),
    [query.pricingMenuItemKey]
  );
  const sectionContainerRef = useRef<HTMLDivElement>(null);

  const { id: jobTypeId } = jobType;
  const handleGoogleAnalytics = useCallback(
    (eventType: string) => {
      logEvent(`LandingPagePro:${jobTypeId}`, `${eventType}`);
    },
    [jobTypeId]
  );

  const handleLoadPage = useCallback(
    ({ pageNumber: newPageNumber, filters }: { filters: FilterState; pageNumber: number }) => {
      const noProsForNewPage = pros.length > 0 && pros.every(({ page }) => page !== newPageNumber);
      const { day, time, priceRange, matchingCriteria: matchingCriteriaObject } = filters;
      const matchingCriteriaQueryParam = unrollMatchingCriteriaFiltersToString(
        matchingCriteriaObject
      );
      if (noProsForNewPage) {
        onLoadMore({
          day,
          matchingCriteria: matchingCriteriaQueryParam,
          page: newPageNumber,
          priceRange: priceRange?.[0],
          pricingMenuItemKey: pricingMenuItemKeyQuery,
          size: numOfProsPerPage,
          skip: numOfProsPerPage * (newPageNumber - 1),
          time,
        });
      }

      updatePageNumber(newPageNumber);

      // Scroll to Pro list section top OR a landingPageFocusElement that may be further up the DOM
      // tree. Currently used to scroll to right above filters so that the style is not sticky
      // The scroll behavior is very important to mitigate CLS while page has changed,
      // because CLS is counted for view port only, so if we scroll to Section Top
      // then show loading placeholders which then made to occupy most of the view port
      // CLS will be minimal to none.
      if (landingPageFocusElement?.current) {
        landingPageFocusElement.current.scrollIntoView();
      } else {
        sectionContainerRef.current?.scrollIntoView();
      }
    },
    [
      landingPageFocusElement,
      onLoadMore,
      pros,
      updatePageNumber,
      numOfProsPerPage,
      pricingMenuItemKeyQuery,
    ]
  );

  useEffect(() => {
    const urlFilters = transformFilterQueryStringToFilterState(url.searchParams.toString());
    const hasFilterChanged = !isEqual(urlFilters, filters);
    if (pageQuery !== currentPage || hasFilterChanged) {
      handleLoadPage({ pageNumber: pageQuery, filters });
    }
  }, [currentPage, handleLoadPage, filters, pageQuery, url.searchParams]);

  if (_.isEmpty(jobType)) {
    return null;
  }

  const pageQueryParamName = isGroupCity ? 'pageGroup' : 'page';
  const testId = isGroupCity ? 'list-pros-group' : 'list-pros';
  const isLoadMoreEnabled = !isGroupCity;

  const pageNumber = isGroupCity ? 1 : currentPage;

  return (
    <CardListV2
      cardRenderer={({ classes, ...dataItem }) => (
        <ProCard
          classes={classes as string}
          jobTypeId={jobTypeId}
          onGoogleAnalytics={handleGoogleAnalytics}
          pro={dataItem as LandingPagePro}
          isReviewsQAEnabled={isReviewsQAEnabled}
        />
      )}
      currentPage={pageNumber}
      data={pros}
      isLoadMoreEnabled={isLoadMoreEnabled}
      loadingPlaceholderHeight="21vh"
      pageQueryParamName={pageQueryParamName}
      pageSize={numOfProsPerPage}
      ref={sectionContainerRef}
      testId={testId}
      totalItems={isGroupCity ? numOfProsPerPage : proCount}
      // ours
      // totalItems={proCount}
    />
  );
};

export type { Props as ListProsProps };
export default ListPros;
